import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { Button, Card, Field, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { Skeleton } from '@/components/States'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { Plastic } from '@/features/cards/Plastic'
import { cn } from '@/lib/cn'
import { formatMoney, fromMajor } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from './Activate.module.css'
import { CardFields, METHODS, MethodPicker, type PayMethod } from './PaymentMethods'

/** The plastic is paid for here, on activation, never when it is ordered. */
const PHYSICAL_FEE = 50

const STAGES = [
  { id: 'printing', label: 'Printed' },
  { id: 'posted', label: 'In the post' },
  { id: 'delivered', label: 'With you' },
] as const

export function ActivateCardScreen() {
  const navigate = useNavigate()
  const { id } = useParams()
  const repository = useRepository()
  const cards = useQuery(() => repository.listCards(), [repository])

  const [last4, setLast4] = useState('')
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [method, setMethod] = useState<PayMethod>('balance')
  const [cardOk, setCardOk] = useState(false)

  const card = cards.data?.find((item) => item.id === id)

  const checks = useMemo(() => {
    const weakPin =
      /^(\d)\1{3}$/.test(pin) || '0123456789'.includes(pin) || '9876543210'.includes(pin)
    return {
      last4Ok: card ? last4 === card.last4 : false,
      last4Touched: last4.length === 4,
      pinOk: /^\d{4}$/.test(pin) && !weakPin,
      weakPin: /^\d{4}$/.test(pin) && weakPin,
      matches: pin.length === 4 && pin === confirm,
    }
  }, [card, last4, pin, confirm])

  if (cards.status === 'loading') return <Skeleton height={360} />
  if (!card) return <NotFoundScreen />
  // Only a plastic card that is on its way is activated (and paid for) here.
  if (card.kind !== 'physical' || card.status !== 'ordered') return <Navigate to="/cards" replace />

  const picked = METHODS[method]
  const methodFee = PHYSICAL_FEE * picked.rate
  const total = fromMajor(PHYSICAL_FEE + methodFee, 'USD')
  const ready = checks.last4Ok && checks.pinOk && checks.matches && (method !== 'card' || cardOk)
  const stageIndex = STAGES.findIndex((s) => s.id === card.delivery?.stage)

  return (
    <>
      <PageHeader
        title="Activate your card"
        subtitle="It only works once you activate it, and the $50 card fee is charged only then, with the card in your hands."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/cards">
            Back to cards
          </Button>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Where it is
              </Text>
              <Pill tone="neutral">{card.delivery?.eta ?? 'On its way'}</Pill>
            </Card.Header>

            <div className={`${styles.track} pb-mt-4`}>
              {STAGES.map((stage, index) => (
                <div key={stage.id} className={styles.step}>
                  <span className={cn(styles.dot, index <= stageIndex && styles.dotOn)}>
                    {index <= stageIndex && <Icon name="check" size={14} />}
                  </span>
                  <Text
                    variant="caption"
                    weight={index === stageIndex ? 'semibold' : 'regular'}
                    tone={index <= stageIndex ? 'primary' : 'secondary'}
                  >
                    {stage.label}
                  </Text>
                </div>
              ))}
            </div>

            {card.delivery && (
              <Stack gap={0} className="pb-mt-4">
                {[
                  ['Carrier', card.delivery.carrier],
                  ['Tracking', card.delivery.tracking],
                  ['Expected', card.delivery.eta],
                ].map(([label, value]) => (
                  <div key={label} className={checkout.line}>
                    <Text variant="caption" tone="secondary">
                      {label}
                    </Text>
                    <Text variant="caption" weight="semibold" numeric>
                      {value}
                    </Text>
                  </div>
                ))}
              </Stack>
            )}
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Confirm you are holding it
            </Text>
            <Text variant="caption" tone="secondary" className="pb-mt-2">
              Type the last four digits printed on the card. We never ask for the full number.
            </Text>
            <div className="pb-mt-4">
              <Field
                label="Last four digits"
                inputMode="numeric"
                maxLength={4}
                value={last4}
                placeholder="••••"
                onChange={(event) => setLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
                {...(checks.last4Touched && !checks.last4Ok
                  ? { error: 'That does not match the card we sent you.' }
                  : {})}
              />
            </div>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Choose a PIN
            </Text>
            <Text variant="caption" tone="secondary" className="pb-mt-2">
              Four digits, for ATMs and chip payments. Not your birth year, and not four of the
              same.
            </Text>
            <Stack gap={4} className="pb-mt-4">
              <Field
                label="New PIN"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 4))}
                {...(checks.weakPin
                  ? { error: 'Too easy to guess. Pick another four digits.' }
                  : {})}
              />
              <Field
                label="Repeat the PIN"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value.replace(/\D/g, '').slice(0, 4))}
                {...(confirm.length === 4 && !checks.matches
                  ? { error: 'The two PINs are different.' }
                  : {})}
              />
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Pay the card fee
            </Text>
            <Text variant="caption" tone="secondary" className="pb-mt-2">
              {formatMoney(fromMajor(PHYSICAL_FEE, 'USD'))} once, now that the card is with you. No
              monthly fee after this.
            </Text>
            <Stack gap={3} className="pb-mt-4">
              <MethodPicker
                options={[METHODS.balance, METHODS.apple, METHODS.google, METHODS.card]}
                value={method}
                onChange={setMethod}
              />
              {method === 'card' && <CardFields onValid={setCardOk} />}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Plastic card={card} />

          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                What happens when you activate
              </Text>
              <Pill tone="accent">Instant</Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {[
                ['The card starts working', 'Right away'],
                ['Your PIN', 'Set on this device'],
                ['Apple, Google and Samsung Pay', 'Ready to add'],
                ['Activation fee · physical card', formatMoney(fromMajor(PHYSICAL_FEE, 'USD'))],
                ...(methodFee
                  ? [
                      [
                        `${picked.label} fee · ${(picked.rate * 100).toFixed(1)}%`,
                        formatMoney(fromMajor(methodFee, 'USD')),
                      ],
                    ]
                  : []),
                ['Charged from', picked.label],
              ].map(([label, value], index) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="onDarkMuted">
                    {label}
                  </Text>
                  <Text
                    variant="caption"
                    weight="semibold"
                    tone={index === 3 ? 'accent' : 'onDark'}
                    numeric
                  >
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                disabled={!ready}
                onClick={() =>
                  navigate(`/activate/card/done?paid=${total.minorUnits}`, { replace: true })
                }
              >
                Pay {formatMoney(total)} and activate
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="info" size={15} />
                <Text variant="caption" tone="onDarkMuted">
                  Not arrived yet? Nothing is charged until you activate it, and you can report it
                  missing from Cards.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
